import { EventObject } from '@xstate/store-react';
import { AppRoute } from '../../../enums/appRoutes';
import { StepKey } from '../../../enums/stepKey';
import { ApplianceFormData } from '../../../models/form/appliance';
import { StoreContext } from '../../../models/store/context';
import { log } from '@domgen/shared/utils/log';
import { posthog } from 'posthog-js';
import { FormKey } from '../../../enums/formKey';
import { QuoteResponse, RegOnlyResponse } from '../../../models/services/api/postQuote';
import { QuoteStatus } from '../../../models/store/quoteStatus';
import { StoreEvent } from '../../../enums/storeEvent';
import { adobeTrackRegistration } from '../../../services/adobeTags/track';
import { getCpgData } from '../../../services/api/getCpgFinder';
import { postQuote } from '../../../services/api/postQuote';
import { postRegistria } from '../../../services/api/postRegistria';
import { capturePostHogEvent } from '../../../services/posthog/capture';
import { getSystemCode } from '../../../utils/getSystemCode';
import { formatQuoteRequest } from '../../../utils/quote/formatQuoteRequest';
import { store } from '../../store';
import { getFormCategoryName } from '../../../utils/store/getFormCategoryName';
import { getFormApplianceName } from '../../../utils/store/getFormApplianceName';

export const submitRegistration = <T extends EventObject = EventObject>(
  context: StoreContext,
  event: T & {
    data: {
      [StepKey.PERSONAL_DETAILS]: ApplianceFormData[StepKey.PERSONAL_DETAILS];
    };
  },
  enqueue: {
    effect: (effect: () => Promise<void>) => void;
    emit: { navigate: (payload: { path: AppRoute; replace?: boolean }) => void };
  },
) => {
  log.debug({ message: StoreEvent.SUBMIT, data: event.data });
  const form = {
    ...context.form,
    ...event.data,
  };

  enqueue.effect(async () => {
    try {
      const systemCode = getSystemCode(context.urlParams?.rs);
      const quoteRequest = formatQuoteRequest({ ...context.form, ...event.data }, systemCode);

      if (!quoteRequest) {
        store.send({ type: StoreEvent.QUOTE_FAILED });
        return;
      }

      const response = await postQuote(quoteRequest);
      const brandCode = import.meta.env.VITE_BRAND_CODE as 'WHIRL' | 'KHAID' | 'MYTAG' | 'AMANA';

      if ('statusCode' in response && response.statusCode === 201) {
        const regOnly = response as RegOnlyResponse;
        store.send({ type: StoreEvent.NO_QUOTE_REG_ONLY, data: regOnly });
        capturePostHogEvent(posthog, 'registration-without-quote', store.get()); // no quote infers a successful registration
        adobeTrackRegistration({
          modelNumber: form[StepKey.MODEL_SERIAL]?.[FormKey.MODEL_NUMBER] ?? '',
          serialNumber: form[StepKey.MODEL_SERIAL]?.[FormKey.SERIAL_NUMBER] ?? '',
          purchasePrice: form[StepKey.PURCHASE_PRICE]?.[FormKey.PURCHASE_PRICE] ?? '',
          dateDelivered: form[StepKey.ARRIVAL_DATE]?.[FormKey.ARRIVAL_DATE] ?? '',
          city: form[StepKey.PERSONAL_DETAILS]?.[FormKey.CITY] ?? '',
          state: form[StepKey.PERSONAL_DETAILS]?.[FormKey.STATE_CODE] ?? '',
          postalCode: form[StepKey.PERSONAL_DETAILS]?.[FormKey.POSTAL_CODE] ?? '',
        });

        const postRegRequest = {
          personId: regOnly.personId,
          brand: brandCode,
          locale: navigator.language.toLowerCase(),
          itemId: regOnly.items?.[0]?.id,
        };

        try {
          const concierge = await postRegistria(postRegRequest);
          store.send({ type: StoreEvent.SET_CONCIERGE_DATA, data: concierge });
        } catch (error) {
          log.error({ message: 'Failed to fetch registria data', data: error });
        }
      } else if ('statusCode' in response && response.statusCode !== 200) {
        console.error('Quote request failed with status code', response.statusCode);
        store.send({ type: StoreEvent.QUOTE_FAILED });
        capturePostHogEvent(posthog, 'registration-failure', store.get());

        return;
      } else {
        const quoteData = response as QuoteResponse;
        store.send({ type: StoreEvent.SET_QUOTE, data: quoteData });
        capturePostHogEvent(posthog, 'registration-with-quote', store.get());
        adobeTrackRegistration({
          modelNumber: form[StepKey.MODEL_SERIAL]?.[FormKey.MODEL_NUMBER] ?? '',
          serialNumber: form[StepKey.MODEL_SERIAL]?.[FormKey.SERIAL_NUMBER] ?? '',
          purchasePrice: form[StepKey.PURCHASE_PRICE]?.[FormKey.PURCHASE_PRICE] ?? '',
          dateDelivered: form[StepKey.ARRIVAL_DATE]?.[FormKey.ARRIVAL_DATE] ?? '',
          city: form[StepKey.PERSONAL_DETAILS]?.[FormKey.CITY] ?? '',
          state: form[StepKey.PERSONAL_DETAILS]?.[FormKey.STATE_CODE] ?? '',
          postalCode: form[StepKey.PERSONAL_DETAILS]?.[FormKey.POSTAL_CODE] ?? '',
        });
        const itemId = quoteData.items?.[0]?.id;
        const postRegRequest = {
          personId: quoteData.personId,
          brand: brandCode,
          locale: navigator.language.toLowerCase(),
          ...(itemId && { itemId }),
        };
        try {
          console.log('Fetching registria data :', postRegRequest);
          const concierge = await postRegistria(postRegRequest);
          console.log('Concierge from reg only:', concierge);
          store.send({ type: StoreEvent.SET_CONCIERGE_DATA, data: concierge });
        } catch (error) {
          log.error({ message: 'Failed to fetch registria data', data: error });
        }
      }

      const modelNumber = form[StepKey.MODEL_SERIAL]?.[FormKey.MODEL_NUMBER];
      if (modelNumber) {
        try {
          const cpgResponse = await getCpgData(modelNumber);
          if (typeof cpgResponse !== 'string') {
            store.send({ type: StoreEvent.SET_CPG_DATA, data: cpgResponse });
          }
        } catch (error) {
          console.error('Failed to fetch CPG data', error);
        }
      }
    } catch (error) {
      console.error('Failed to submit quote', error);
      store.send({ type: StoreEvent.QUOTE_FAILED });
    }
  });

  const updatedContext = {
    ...context,
    form,
    quote: { status: QuoteStatus.LOADING },
    confirmation: {
      applianceDetails: {
        applianceName: getFormApplianceName(context),
        categoryName: getFormCategoryName(context),
        serialNumber: form[StepKey.MODEL_SERIAL]?.[FormKey.SERIAL_NUMBER],
        modelNumber: form[StepKey.MODEL_SERIAL]?.[FormKey.MODEL_NUMBER],
        arrivalDate: form[StepKey.ARRIVAL_DATE]?.[FormKey.ARRIVAL_DATE],
        purchasePrice: form[StepKey.PURCHASE_PRICE]?.[FormKey.PURCHASE_PRICE],
      },
    },
  };

  return updatedContext;
};
